import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { advisorAccountActivationApi, advisorAccountActivationKeys } from '@client/api/waterlily';
import { useRoleSwitch } from '@client/hooks/useRoleSwitch';
import type { ClientUserMenuAction } from '@client/components/ClientUserMenu';

export function useAdvisorAccountEntry({
  enabled,
  advisorAccount,
}: {
  enabled: boolean;
  advisorAccount: Session['advisorAccount'];
}) {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const { switchRole } = useRoleSwitch();
  const preflight = useQuery({
    queryKey: advisorAccountActivationKeys.preflight(),
    queryFn: () => advisorAccountActivationApi.getPreflight(),
    enabled: enabled && !advisorAccount,
    staleTime: 30_000,
    retry: false,
  });

  const canCreateAdvisorProfile =
    enabled && !advisorAccount && preflight.isSuccess && preflight.data.status === 'eligible';

  const advisorAccountAction: ClientUserMenuAction | undefined = advisorAccount
    ? {
        label:
          advisorAccount.onboardingStatus === 'complete'
            ? 'Switch to advisor view'
            : 'Continue advisor setup',
        description:
          advisorAccount.onboardingStatus === 'complete'
            ? 'Manage clients and plans'
            : 'Finish setting up your advisor account',
        onClick: () => switchRole('advisor', advisorAccount.resumePath),
      }
    : canCreateAdvisorProfile
      ? {
          label: 'Create advisor account',
          description: 'For financial professionals',
          onClick: () => setIsConfirmationOpen(true),
        }
      : undefined;

  return {
    advisorAccountAction,
    preflight: preflight.data ?? null,
    isConfirmationOpen,
    setConfirmationOpen: setIsConfirmationOpen,
  };
}
